import { useEffect, useState, type ReactNode } from 'react';
import {
  AIDLC_FULL_NAME,
  AIDLC_NAME,
  PRODUCT_BY_ID,
  type ProductId,
} from '@/config/products';

interface AuthSingleCardLayoutProps {
  /** Optional pill above the title (e.g. register flow). */
  badge?: string;
  title: string;
  subtitle: string;
  children: ReactNode;
  /** Kept for call-site compat. */
  hideProductPills?: boolean;
  containerWidthClass?: string;
  /** Branded login entry — ?product=code or code.cotaskr.com */
  entryProductId?: ProductId;
  /** Kept for call-site compat. */
  brandMode?: 'signin' | 'signup';
}

/** Inner AIDLC orbit — Code → Gate → Ship → Lenc → Remo */
const LOOP_NODES = [
  {
    key: 'code',
    label: 'CodeFx',
    color: '#60A5FA',
    angle: -90,
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <path d="M6 7L2 10l4 3" /><path d="M14 7l4 3-4 3" /><path d="M12 5l-4 10" />
      </svg>
    ),
    hubLine1: 'Scanning code',
    hubLine2: 'finding issues…',
  },
  {
    key: 'gate',
    label: 'Gate',
    color: '#34D399',
    angle: -18,
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <path d="M10 2L4 5v5c0 4.4 3.1 7 6 8 2.9-1 6-3.6 6-8V5l-6-3z" /><path d="M7.5 10l2 2 3-3.5" />
      </svg>
    ),
    hubLine1: 'Confidence gate',
    hubLine2: 'checking score…',
  },
  {
    key: 'ship',
    label: 'Ship',
    color: '#2DD4BF',
    angle: 54,
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <path d="M3 13l1.5 3h11L17 13" />
        <path d="M2 13h16" />
        <path d="M5 13V9h10v4" />
        <path d="M10 9V4" />
        <path d="M10 4l4 3" />
      </svg>
    ),
    hubLine1: 'Deploying',
    hubLine2: 'to production…',
  },
  {
    key: 'lenc',
    label: 'Lenc',
    color: '#A78BFA',
    angle: 126,
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <rect x="2" y="3" width="16" height="11" rx="1.5" /><path d="M2 17h16M8 17v-3M12 17v-3" />
        <path d="M4 10l2.5-3.5 2 2.5 2-4 2.5 3.5L15 10" />
      </svg>
    ),
    hubLine1: 'Monitoring',
    hubLine2: 'infra health…',
  },
  {
    key: 'remo',
    label: 'Remo',
    color: '#FB923C',
    angle: 198,
    icon: (
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
        <rect x="4" y="7" width="12" height="9" rx="2" />
        <circle cx="8" cy="11" r="1.2" fill="currentColor" stroke="none" />
        <circle cx="12" cy="11" r="1.2" fill="currentColor" stroke="none" />
        <path d="M10 3v4M7 3h6" /><path d="M6 16v2M14 16v2" strokeOpacity="0.6" />
      </svg>
    ),
    hubLine1: 'Remo agent',
    hubLine2: 'auto-fixing…',
  },
] satisfies Array<{ key: string; label: string; color: string; angle: number; icon: ReactNode; hubLine1: string; hubLine2: string }>;

/** Plan lives on the outer orbit — receives tasks pushed from every inner node */
const PLAN_NODE = {
  key: 'plan',
  label: 'Plan',
  color: '#F472B6',
  angle: 234, // upper-left — sits in the gap between Remo (198°) and Code (-90°/270°)
  icon: (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
      <rect x="3" y="4" width="14" height="13" rx="2" />
      <path d="M7 2v4M13 2v4M3 9h14" />
      <path d="M7 13h2M7 16h4" />
    </svg>
  ),
};

const LOOP_STEPS: Array<{
  hubLine1: string;
  hubLine2: string;
  active: 'code' | 'gate' | 'ship' | 'lenc' | 'remo' | 'all-green' | 'incident' | 'plan-push';
  tone: 'idle' | 'ok' | 'warn' | 'fix' | 'plan';
}> = [
  { hubLine1: 'Scanning code',    hubLine2: 'finding issues…',     active: 'code',      tone: 'idle' },
  { hubLine1: 'Confidence gate',  hubLine2: 'checking score…',     active: 'gate',      tone: 'idle' },
  { hubLine1: 'Gate passed ✓',    hubLine2: 'ready to ship',       active: 'gate',      tone: 'ok'   },
  { hubLine1: 'Deploying',        hubLine2: 'to production…',      active: 'ship',      tone: 'ok'   },
  { hubLine1: 'Monitoring',       hubLine2: 'infra health…',       active: 'lenc',      tone: 'ok'   },
  { hubLine1: 'Incident',         hubLine2: 'detected ⚠',          active: 'incident',  tone: 'warn' },
  { hubLine1: 'Remo agent',       hubLine2: 'auto-fixing…',        active: 'remo',      tone: 'fix'  },
  { hubLine1: 'Fix PR opened',    hubLine2: '→ pushed to Plan',    active: 'plan-push', tone: 'plan' },
  { hubLine1: 'Code re-scanning', hubLine2: 'the fix…',            active: 'code',      tone: 'idle' },
  { hubLine1: 'Loop closed ✓',    hubLine2: 'billed & tracked',    active: 'all-green', tone: 'ok'   },
];

/**
 * Left-panel centerpiece — dual-orbit: inner AIDLC loop + outer Plan orbit.
 * Center hub text is derived directly from the active step for perfect sync.
 */
function BrandCenterpiece() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    const id = window.setInterval(() => {
      setStep((s) => (s + 1) % LOOP_STEPS.length);
    }, 1800);
    return () => window.clearInterval(id);
  }, []);

  const current = LOOP_STEPS[step];
  // Geometry
  const size  = 320;
  const cx    = size / 2;  // 160
  const cy    = size / 2;  // 160
  const inner = 100;       // inner orbit radius (nodes)
  const outer = 148;       // outer orbit radius (Plan)

  // Plan node position
  const planRad = (PLAN_NODE.angle * Math.PI) / 180;
  const planX   = cx + outer * Math.cos(planRad);
  const planY   = cy + outer * Math.sin(planRad);

  const isPlanActive  = current.active === 'plan-push' || current.active === 'all-green';
  const isAllGreen    = current.active === 'all-green';
  const isPlanPush    = current.active === 'plan-push';

  // Compute active inner node for the glow dot
  const activeInnerKey =
    current.active === 'incident'  ? 'lenc' :
    current.active === 'plan-push' ? 'remo' :
    current.active === 'all-green' ? null   : current.active;
  const activeInnerNode = activeInnerKey
    ? LOOP_NODES.find((n) => n.key === activeInnerKey) ?? LOOP_NODES[0]
    : null;
  const dotRad  = activeInnerNode ? (activeInnerNode.angle * Math.PI) / 180 : 0;
  const dotX    = activeInnerNode ? cx + inner * Math.cos(dotRad) : cx;
  const dotY    = activeInnerNode ? cy + inner * Math.sin(dotRad) : cy;

  // Tone-derived hub colors
  const hubBorder =
    current.tone === 'warn' ? 'rgba(248,113,113,0.70)' :
    current.tone === 'ok'   ? 'rgba(52,211,153,0.65)'  :
    current.tone === 'fix'  ? 'rgba(251,146,60,0.65)'  :
    current.tone === 'plan' ? 'rgba(244,114,182,0.65)' :
    'rgba(96,165,250,0.55)';

  return (
    <div className="auth-brand-center relative mx-auto mt-4 w-[min(360px,96%)]" aria-hidden>
      <div className="auth-brand-glow pointer-events-none absolute inset-[15%] rounded-full bg-[#1D4ED8]/20 blur-3xl" />

      <div className="relative mx-auto aspect-square w-full max-w-[340px]">
        <svg viewBox={`0 0 ${size} ${size}`} className="absolute inset-0 h-full w-full overflow-visible">
          <defs>
            <marker id="arrowPlan" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
              <path d="M0,0 L0,6 L6,3 z" fill="#F472B6" opacity="0.7" />
            </marker>
          </defs>

          {/* ── Outer orbit ring (Plan) ── */}
          <circle cx={cx} cy={cy} r={outer} fill="none"
            stroke="rgba(244,114,182,0.14)" strokeWidth="1.5" strokeDasharray="3 7" />

          {/* ── Connector spokes: each inner node → Plan ── */}
          {LOOP_NODES.map((node) => {
            const rad = (node.angle * Math.PI) / 180;
            const nx  = cx + inner * Math.cos(rad);
            const ny  = cy + inner * Math.sin(rad);
            const dx  = planX - nx; const dy = planY - ny;
            const len = Math.sqrt(dx * dx + dy * dy);
            const ux  = dx / len;   const uy = dy / len;
            return (
              <line key={node.key}
                x1={nx + ux * 22} y1={ny + uy * 22}
                x2={planX - ux * 22} y2={planY - uy * 22}
                stroke="#F472B6"
                strokeWidth={isPlanPush || isAllGreen ? 1.4 : 0.5}
                strokeOpacity={isPlanPush || isAllGreen ? 0.55 : 0.10}
                strokeDasharray={isPlanPush || isAllGreen ? '4 4' : '2 6'}
                markerEnd={isPlanPush || isAllGreen ? 'url(#arrowPlan)' : undefined}
                style={{ transition: 'stroke-opacity 0.5s ease, stroke-width 0.5s ease' }}
              />
            );
          })}

          {/* ── Inner orbit ring ── */}
          <circle cx={cx} cy={cy} r={inner} fill="none"
            stroke="rgba(147,197,253,0.15)" strokeWidth="1.5" strokeDasharray="4 6" />
        </svg>

        {/* ── Controlled sync pulse dot — moves to active node, no CSS spin ── */}
        {activeInnerNode && (
          <div
            className="pointer-events-none absolute rounded-full"
            style={{
              width: 44,
              height: 44,
              left: `${(dotX / size) * 100}%`,
              top:  `${(dotY / size) * 100}%`,
              transform: 'translate(-50%, -50%)',
              background: `radial-gradient(circle, ${activeInnerNode.color}55 0%, transparent 70%)`,
              boxShadow:  `0 0 28px 6px ${activeInnerNode.color}44`,
              transition: 'left 0.55s cubic-bezier(.4,0,.2,1), top 0.55s cubic-bezier(.4,0,.2,1)',
              zIndex: 0,
            }}
          />
        )}

        {/* ── Inner orbit nodes ── */}
        {LOOP_NODES.map((node) => {
          const rad = (node.angle * Math.PI) / 180;
          const x   = cx + inner * Math.cos(rad);
          const y   = cy + inner * Math.sin(rad);
          const isActive =
            current.active === node.key ||
            isAllGreen ||
            (current.active === 'incident' && node.key === 'lenc');
          const isIncident = current.active === 'incident' && node.key === 'lenc';
          return (
            <div
              key={node.key}
              className={`auth-loop-node absolute flex w-[68px] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[3px] ${
                isActive ? 'auth-loop-node-active' : ''
              }`}
              style={{ left: `${(x / size) * 100}%`, top: `${(y / size) * 100}%` }}
            >
              <span
                className="flex h-9 w-9 items-center justify-center rounded-xl border"
                style={{
                  color:        isIncident ? '#F87171' : node.color,
                  borderColor:  isIncident ? 'rgba(248,113,113,0.65)' : `${node.color}66`,
                  background:   isIncident ? 'rgba(248,113,113,0.15)' : `${node.color}18`,
                  boxShadow:    isActive   ? `0 0 20px ${node.color}55` : undefined,
                  transition:   'box-shadow 0.4s ease',
                }}
              >
                {node.icon}
              </span>
              <span className="text-[0.55rem] font-semibold uppercase tracking-[0.1em]"
                style={{ color: isIncident ? '#FCA5A5' : node.color }}>
                {node.label}
              </span>
            </div>
          );
        })}

        {/* ── Outer orbit — Plan node ── */}
        <div
          className={`auth-loop-node absolute flex w-[72px] -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-[3px] ${
            isPlanActive ? 'auth-loop-node-active' : ''
          }`}
          style={{ left: `${(planX / size) * 100}%`, top: `${(planY / size) * 100}%` }}
        >
          <span
            className="flex h-10 w-10 items-center justify-center rounded-xl border"
            style={{
              color:       PLAN_NODE.color,
              borderColor: `${PLAN_NODE.color}66`,
              background:  `${PLAN_NODE.color}18`,
              boxShadow:   isPlanActive ? `0 0 26px ${PLAN_NODE.color}66` : undefined,
              transition:  'box-shadow 0.4s ease',
            }}
          >
            {PLAN_NODE.icon}
          </span>
          <span className="text-[0.55rem] font-semibold uppercase tracking-[0.1em]"
            style={{ color: PLAN_NODE.color }}>
            {PLAN_NODE.label}
          </span>
          {isPlanActive && (
            <span className="mt-0.5 rounded-full bg-pink-500/20 px-1.5 py-[1px] text-[0.5rem] font-bold text-pink-300">
              {isAllGreen ? 'tracked' : 'task in'}
            </span>
          )}
        </div>

        {/* ── Center hub — text synced to active step ── */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div
            className={`auth-hub-ring relative flex h-[100px] w-[100px] flex-col items-center justify-center rounded-full border-2 px-2 text-center ${
              current.tone === 'warn' ? 'auth-hub-warn' :
              current.tone === 'ok'   ? 'auth-hub-ok'   :
              current.tone === 'fix'  ? 'auth-hub-fix'  :
              current.tone === 'plan' ? 'auth-hub-plan'  : ''
            }`}
            style={{
              background:  'linear-gradient(135deg,#112169,#0e1e52)',
              borderColor: hubBorder,
              transition:  'border-color 0.4s ease, box-shadow 0.4s ease',
            }}
          >
            <span className="auth-ink-ripple pointer-events-none absolute inset-0 rounded-full border-2 border-[#93C5FD]" />
            <p className="relative z-[1] text-[0.6rem] font-bold leading-tight text-white/90 transition-all duration-300">
              {current.hubLine1}
            </p>
            <p className="relative z-[1] mt-0.5 text-[0.55rem] leading-tight text-white/55 transition-all duration-300">
              {current.hubLine2}
            </p>
          </div>
        </div>
      </div>

      <p className="mt-2 text-center text-[0.65rem] tracking-wide text-white/35">
        Code → Gate → Ship → Lenc → Remo → Plan
      </p>
    </div>
  );
}
function CoTaskrMark({
  tone = 'form',
  size = 'md',
}: {
  tone?: 'form' | 'hero';
  size?: 'md' | 'lg';
}) {
  const isHero = tone === 'hero';
  const word = isHero ? 'text-white' : 'text-[#1D4ED8]';
  const scale =
    size === 'lg'
      ? { word: 'text-[1.85rem]', orb: 'h-11 w-11', icon: 'h-5 w-5', gap: 'mx-1.5' }
      : { word: 'text-[1.85rem]', orb: 'h-[2.55rem] w-[2.55rem]', icon: 'h-[1.35rem] w-[1.35rem]', gap: 'mx-1' };

  return (
    <div className="inline-flex items-center justify-center">
      <span className={`${scale.word} font-bold leading-none tracking-tight ${word}`}>C</span>
      <span
        className={`auth-logo-orb relative ${scale.gap} flex ${scale.orb} shrink-0 items-center justify-center rounded-full bg-[#1D4ED8] ${
          isHero ? 'shadow-[0_10px_28px_rgba(29,78,216,.5)]' : 'shadow-[0_6px_16px_rgba(29,78,216,.28)]'
        }`}
        aria-hidden
      >
        <span className="auth-ink-ripple pointer-events-none absolute inset-0 rounded-full border-2 border-[#93C5FD]" />
        <svg viewBox="0 0 24 24" className={`relative ${scale.icon} text-white`} fill="none" stroke="currentColor">
          <path
            className="auth-ink-tick"
            pathLength={1}
            strokeDasharray="1"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3.2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </span>
      <span className={`${scale.word} font-bold lowercase leading-none tracking-tight ${word}`}>taskr</span>
    </div>
  );
}


/** Form-side CoTaskr lockup — centered above Welcome back. */
function AuthLockup() {
  return (
    <div className="mb-1.5 flex flex-col items-center text-center">
      <CoTaskrMark tone="form" size="md" />
      <span className="mt-1.5 block text-[0.62rem] uppercase tracking-[0.12em] text-[#64748B] font-[family-name:var(--font-mono)]">
        {AIDLC_NAME} workspace
      </span>
    </div>
  );
}

const TRUST_ITEMS = [
  {
    label: 'SOC 2 Type II · in progress',
    icon: <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />,
  },
  {
    label: 'SSO · SAML · 2FA',
    icon: (
      <>
        <rect x="5" y="10" width="14" height="10" rx="2" />
        <path d="M8 10V7a4 4 0 018 0v3" />
      </>
    ),
  },
  {
    label: 'DPDP & PDPL aligned',
    icon: <path d="M12 3L4 6v5c0 5 3.4 8.7 8 10 4.6-1.3 8-5 8-10V6l-8-3z" />,
  },
];

export default function AuthSingleCardLayout({
  badge,
  title,
  subtitle,
  children,
  entryProductId = 'pm',
}: AuthSingleCardLayoutProps) {
  const product = PRODUCT_BY_ID[entryProductId];
  const isPlan = entryProductId === 'pm';

  return (
    <div className="auth-ink relative min-h-screen bg-[#EEF3FB] font-[family-name:var(--font)] text-[#0F172A]">
      <div className="grid min-h-screen lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
        {/* ── Brand / signature panel ── */}
        <aside
          className="relative hidden flex-col overflow-hidden p-[clamp(2rem,4vw,3.75rem)] text-white lg:flex"
          style={{
            background:
              'radial-gradient(1100px 600px at 12% -10%, rgba(37,99,235,0.35) 0%, transparent 58%), radial-gradient(900px 500px at 88% 110%, rgba(29,78,216,0.22) 0%, transparent 55%), linear-gradient(180deg, #0B1B3A 0%, #0A1628 100%)',
          }}
        >
          <div className="flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.14em] text-[#93C5FD]">
            <span className="h-px w-[22px] bg-[#93C5FD] opacity-60" />
            {AIDLC_NAME}
          </div>
          <p className="mt-1.5 text-[0.78rem] text-white/45">{AIDLC_FULL_NAME}</p>

          <h1 className="mb-3 mt-8 max-w-[22ch] text-[clamp(1.7rem,1.05rem+2vw,2.35rem)] font-semibold leading-[1.14] tracking-[-0.01em]">
            Every release, one accountable loop.
          </h1>
          <p className="max-w-[40ch] text-[0.98rem] leading-relaxed text-white/60">
            Plan the work. Gate the deploy on live confidence. When production falters, Remo fixes it —
            and the fix is re-scanned before it ships again.
          </p>

          {!isPlan && (
            <p className="mt-4 text-[0.82rem] text-[#93C5FD]">
              Opening {product.name} — same workspace login.
            </p>
          )}

          <BrandCenterpiece />

          <div className="mt-auto flex flex-wrap gap-x-5 gap-y-2 border-t border-white/10 pt-7">
            {TRUST_ITEMS.map((item) => (
              <span
                key={item.label}
                className="flex items-center gap-1.5 text-[0.68rem] uppercase tracking-[0.05em] text-white/50 font-[family-name:var(--font-mono)]"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="#93C5FD" strokeWidth="1.6" className="h-[13px] w-[13px] shrink-0">
                  {item.icon}
                </svg>
                {item.label}
              </span>
            ))}
          </div>
          <p className="mt-4 text-center text-[0.72rem] text-white/30">© 2026 AiLenc Technologies Pvt. Ltd.</p>
        </aside>

        {/* ── Auth panel ── */}
        <main className="flex items-center justify-center bg-[#EEF3FB] px-6 py-10 sm:px-8">
          <div className="w-full max-w-[392px]">
            <AuthLockup />

            {badge ? (
              <div className="mb-4 mt-2 flex justify-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#BFDBFE] bg-[#EFF6FF] px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#1D4ED8]">
                  {badge}
                </div>
              </div>
            ) : null}

            <h2 className="mt-4 text-center text-2xl font-semibold tracking-[-0.01em] text-[#0F172A]">{title}</h2>
            <p className="mt-1.5 text-center text-[0.92rem] leading-relaxed text-[#5B6478]">{subtitle}</p>

            <div className="mt-6">{children}</div>
          </div>
        </main>
      </div>

      <style>{`
        .auth-brand-glow {
          animation: authBrandGlow 5s ease-in-out infinite;
        }
        @keyframes authBrandGlow {
          0%, 100% { opacity: .45; transform: scale(1); }
          50% { opacity: .75; transform: scale(1.06); }
        }

        .auth-loop-node {
          opacity: 0.68;
          transition: opacity 0.35s ease, transform 0.35s ease;
        }
        .auth-loop-node-active {
          opacity: 1;
          transform: translate(-50%, -50%) scale(1.1);
        }

        .auth-hub-ring {
          animation: authHubPulse 4s ease-in-out infinite;
          transition: border-color 0.4s ease, box-shadow 0.4s ease;
        }
        .auth-hub-ok   { box-shadow: 0 0 28px rgba(52,211,153,.45)  !important; }
        .auth-hub-warn { box-shadow: 0 0 28px rgba(248,113,113,.45) !important; }
        .auth-hub-fix  { box-shadow: 0 0 28px rgba(251,146,60,.45)  !important; }
        .auth-hub-plan { box-shadow: 0 0 28px rgba(244,114,182,.55) !important; }
        @keyframes authHubPulse {
          0%, 100% { box-shadow: 0 0 22px rgba(29,78,216,.5); }
          50%      { box-shadow: 0 0 38px rgba(29,78,216,.8), 0 0 60px rgba(29,78,216,.25); }
        }

        .auth-logo-orb { position: relative; }
        .auth-ink-ripple {
          animation: authInkRipple 3s ease-out infinite;
        }
        @keyframes authInkRipple {
          0%, 35% { transform: scale(0.92); opacity: 0; }
          12% { opacity: .55; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        .auth-ink-tick { animation: authInkTick 3s ease-in-out infinite; }
        @keyframes authInkTick {
          0%, 12% { stroke-dashoffset: 1; }
          45%, 82% { stroke-dashoffset: 0; }
          100% { stroke-dashoffset: 1; }
        }

        .auth-ink input[type="text"],
        .auth-ink input[type="email"],
        .auth-ink input[type="password"] {
          background: #FFFFFF;
          border-color: #D7E3F4;
          border-radius: 10px;
          color: #0F172A;
        }
        .auth-ink input[type="text"]:hover,
        .auth-ink input[type="email"]:hover,
        .auth-ink input[type="password"]:hover { border-color: #B6CBE8; }
        .auth-ink input[type="text"]:focus,
        .auth-ink input[type="email"]:focus,
        .auth-ink input[type="password"]:focus {
          border-color: #93C5FD !important;
          box-shadow: 0 0 0 3px rgba(59,130,246,.22) !important;
          outline: none;
        }
        .auth-ink input[type="checkbox"] { accent-color: #1D4ED8; }
        .auth-ink form button[type="submit"] {
          background: #1D4ED8 !important;
          border-color: #1D4ED8 !important;
          border-radius: 10px;
          color: #FFFFFF !important;
        }
        .auth-ink form button[type="submit"]:hover:not(:disabled) { background: #1E40AF !important; border-color: #1E40AF !important; }
        .auth-ink .text-brand { color: #1D4ED8 !important; }
        .auth-ink .text-brand:hover,
        .auth-ink .hover\\:text-brand-dark:hover { color: #1E40AF !important; }

        @media (prefers-reduced-motion: reduce) {
          .auth-brand-glow,
          .auth-hub-ring { animation: none; }
          .auth-ink-ripple, .auth-ink-tick { animation: none; opacity: 0; stroke-dashoffset: 0; }
          .auth-loop-node { transition: none; }
        }
      `}</style>
    </div>
  );
}
